/**
 * AO HOA - KHONG PHAI TOI UU, MA LA DIEU KIEN DE SAN PHAM TON TAI.
 *
 * Mot trang A4 o 2x DPR chiem khoang 15 MB bitmap chua nen. Mot tai lieu 300
 * trang ma ve het la 4.5 GB. Khong co thiet bi nao chiu duoc. Rang buoc o day
 * la BO NHO, khong phai CPU - va do la ly do moi quyet dinh trong tep nay.
 *
 * Bon co che:
 *
 *  1. Kich thuoc trang duoc lay TRUOC (DocumentInfo.pages) nen tong chieu cao
 *     vung cuon la con so co dinh ngay tu dau. Khong phai phan tich trang nao
 *     de biet thanh cuon dai bao nhieu, va thanh cuon khong bao gio nhay.
 *
 *  2. Chi nhung trang trong vung nhin CONG hai trang dem moi duoc dung. Cac
 *     trang khac chi la mot the div rong dung kich thuoc - giu bo cuc, ton
 *     vai chuc byte.
 *
 *  3. Canvas duoc muon tu be chua va tra lai (xem canvasPool.ts).
 *
 *  4. Khi cuon nhanh, ve ban do phan giai thap; khi dung lai qua nguong thoi
 *     gian thi nang cap len do phan giai day du. Nguoi dung cuon nhanh khong
 *     doc chu, ho tim vi tri - ho can khung hinh muot, khong can net.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Annotation, PdfCore, PdfPoint, PdfRect, TextBlock } from '@pdfeditor/types';
import { PageView, type EmbeddedImageBridge, type NoteDraft } from './PageView';
import type { EditScope } from '../core/edit/scope';
import type { EditRenderHint, EditedItem } from './EditedTextLayer';
import { displaySize, type PageGeometry } from './geometry';
import { firstSlotForOriginalPage, type ViewSlot } from './planView';
import type { PlanDocuments } from './usePlanDocuments';
import type { EditSession } from '../ui/InlineTextEditor';
import type { TextBoxDraftStyle } from './NotePopup';
import type { TextAlign } from '../ui/textEditCore';
import type { ShapeTool, ToolId } from '../state';

/** So trang dem moi ben ngoai vung nhin. */
const BUFFER_PAGES = 2;
/** Khoang trang giua hai trang, CSS pixel. */
const PAGE_GAP = 20;
/** Cuon dung yen bao lau thi nang cap tu ban nhap len ban day du. */
const SETTLE_MS = 160;

export interface VirtualizedDocumentProps {
  core: PdfCore | null;
  /**
   * O TRANG DA PHAN GIAI, theo dung thu tu cua so do trang.
   *
   * Day la thu duy nhat quyet dinh khung xem ve gi. Truoc day cho nay nhan
   * `info` cua tai lieu da mo luc dau, nen moi thao tac trang deu chi doi thanh
   * ben con khung xem thi khong: ghep them mot tep vao va bo dem van ghi "/ 3"
   * trong khi tep xuat ra co 4 trang. Xem viewer/planView.ts.
   */
  slots: readonly ViewSlot[];
  /** Tai lieu pdf.js theo ma nguon - trang ghep den tu tai lieu khac. */
  documents: PlanDocuments;
  zoom: number;
  annotations: readonly Annotation[];
  selectedBlockId: string | null;
  selectedScope: EditScope | null;
  onSelectBlock: (block: TextBlock | null, scope: EditScope | null) => void;
  onBlocksLoaded: (pageIndex: number, blocks: TextBlock[]) => void;
  onCurrentPageChange: (pageIndex: number) => void;
  /** Yeu cau cuon den trang nay (tang bo dem moi lan yeu cau, ke ca cung trang). */
  scrollRequest: { pageIndex: number; nonce: number } | null;

  // --- Chinh sua van ban ---------------------------------------------------
  tool: ToolId;
  editSession: EditSession | null;
  edited: ReadonlyMap<string, EditedItem>;
  editHints: ReadonlyMap<string, EditRenderHint>;
  onRequestEdit: (block: TextBlock, scope: EditScope) => void;
  editRevision: number;

  // --- Chu thich ------------------------------------------------------------
  onInkStroke?: (pageIndex: number, path: PdfPoint[]) => void;
  onNotePoint?: (pageIndex: number, point: PdfPoint) => void;
  onSelectAnnotation?: (id: string) => void;
  selectedAnnotationId?: string | null;
  onDeselectAnnotation?: () => void;
  /** Doi tuong da doi hop chu nhat (keo di cho hoac keo tay nam). */
  onChangeAnnotationRect?: (id: string, rect: PdfRect) => void;
  onDeleteAnnotation?: (id: string) => void;
  onShape?: (tool: ShapeTool, pageIndex: number, rect: PdfRect) => void;
  /** Nguoi dung da chi cho dat anh. rect null = chi bam, dat o co tu nhien. */
  onImageRect?: (pageIndex: number, rect: PdfRect | null, point: PdfPoint) => void;
  /** Dia chi hien thi cua mot anh da ghi nhan, tra cuu theo ma. */
  imageUrl?: (ref: string) => string | undefined;
  noteDraft?: NoteDraft | null;
  /** Kieu chu LIVE cua o chu giai, do App giu (bang thuoc tinh o popup sua). */
  noteStyle?: TextBoxDraftStyle;
  onNoteSave?: (text: string) => void;
  /** Phim tat can le trong o go goi vao day de doi kieu chu o App. */
  onNoteAlignChange?: (align: TextAlign) => void;
  onNoteCancel?: () => void;
  /** Anh da nhung san trong trang. Bo qua = tat tinh nang do. */
  embeddedImages?: EmbeddedImageBridge;
}

/** Mot o trang da co ca vi tri trong vung cuon. */
interface LaidOutSlot {
  slot: ViewSlot;
  index: number;
  top: number;
  width: number;
  height: number;
  geometry: PageGeometry;
}

export function VirtualizedDocument({
  core,
  slots: viewSlots,
  documents,
  zoom,
  annotations,
  selectedBlockId,
  selectedScope,
  onSelectBlock,
  onBlocksLoaded,
  onCurrentPageChange,
  scrollRequest,
  tool,
  editSession,
  edited,
  editHints,
  onRequestEdit,
  editRevision,
  onInkStroke,
  onNotePoint,
  onSelectAnnotation,
  selectedAnnotationId,
  onDeselectAnnotation,
  onChangeAnnotationRect,
  onDeleteAnnotation,
  onShape,
  onImageRect,
  imageUrl,
  noteDraft,
  noteStyle,
  onNoteSave,
  onNoteAlignChange,
  onNoteCancel,
  embeddedImages,
}: VirtualizedDocumentProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [fastScrolling, setFastScrolling] = useState(false);

  const lastScrollRef = useRef({ top: 0, time: 0 });
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef(0);

  // Theo doi kich thuoc khung xem. ResizeObserver chu khong phai su kien resize
  // cua window: thanh ben co the dong/mo ma cua so khong doi kich thuoc.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      setViewportHeight(el.clientHeight);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /**
   * Ti le hien thi. Truoc day co che do "vua be rong" tu tinh tu trang rong
   * nhat; da bo theo yeu cau nguoi dung, nen ti le thuc te chinh la muc zoom.
   */
  const effectiveZoom = zoom;

  /**
   * Bo cuc doc duoc tinh mot lan cho moi muc zoom. O(so trang) va chi la so
   * hoc - 5000 trang van duoi mot mili giay.
   */
  const { slots, totalHeight } = useMemo(() => {
    const out: LaidOutSlot[] = [];
    let top = PAGE_GAP;
    for (const s of viewSlots) {
      const geometry: PageGeometry = {
        widthPt: s.widthPt,
        heightPt: s.heightPt,
        // Do xoay o day DA la tong cua /Rotate trong trang va phan xoay them.
        // Ca ba lop (canvas, van ban, chu thich) deu doc ra tu day, nen chung
        // khong the lech nhau.
        rotation: s.rotation,
        zoom: effectiveZoom,
      };
      const size = displaySize(geometry);
      out.push({
        slot: s,
        index: s.slotIndex,
        top,
        width: size.width,
        height: size.height,
        geometry,
      });
      top += size.height + PAGE_GAP;
    }
    return { slots: out, totalHeight: top };
  }, [viewSlots, effectiveZoom]);

  /**
   * O trang DAU TIEN mang moi trang goc.
   *
   * Mot trang goc co the nam o hai o (nhan doi trang la thao tac hop le). O
   * soan tai cho va o chu giai chi duoc hien o MOT cho - hai o soan cho cung
   * mot lan sua la hai cho de go ma chi mot cho co tac dung.
   */
  const editorSlotIndex = useMemo(() => {
    if (editSession) return firstSlotForOriginalPage(viewSlots, editSession.pageIndex);
    return null;
  }, [viewSlots, editSession]);

  const noteSlotIndex = useMemo(() => {
    if (noteDraft) return firstSlotForOriginalPage(viewSlots, noteDraft.pageIndex);
    return null;
  }, [viewSlots, noteDraft]);

  // Cuon: gop vao mot khung hinh, va do van toc de quyet dinh che do ban nhap.
  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const now = performance.now();
    const top = el.scrollTop;
    const prev = lastScrollRef.current;
    const dt = now - prev.time;
    if (dt > 0) {
      const velocity = Math.abs(top - prev.top) / dt; // px / ms
      // Nguong 1.5 px/ms ~ 1500 px/giay: nhanh hon toc do doc.
      if (velocity > 1.5) setFastScrolling(true);
    }
    lastScrollRef.current = { top, time: now };

    if (rafRef.current === 0) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        setScrollTop(el.scrollTop);
      });
    }

    if (settleTimerRef.current !== null) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      setFastScrolling(false);
    }, SETTLE_MS);
  }, []);

  useEffect(
    () => () => {
      if (settleTimerRef.current !== null) clearTimeout(settleTimerRef.current);
      if (rafRef.current !== 0) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  /** Khoang trang can dung: vung nhin + dem hai ben. */
  const range = useMemo(() => {
    if (slots.length === 0) return { start: 0, end: -1 };
    const viewTop = scrollTop;
    const viewBottom = scrollTop + Math.max(viewportHeight, 1);

    let start = slots.length - 1;
    let end = 0;
    for (const slot of slots) {
      const slotBottom = slot.top + slot.height;
      if (slotBottom >= viewTop && slot.top <= viewBottom) {
        start = Math.min(start, slot.index);
        end = Math.max(end, slot.index);
      }
    }
    if (start > end) {
      // Khong trang nao giao vung nhin (co the dang o khe giua hai trang).
      start = end = nearestIndex(slots, viewTop);
    }
    return {
      start: Math.max(0, start - BUFFER_PAGES),
      end: Math.min(slots.length - 1, end + BUFFER_PAGES),
    };
  }, [slots, scrollTop, viewportHeight]);

  // Trang hien hanh = trang chiem nhieu dien tich vung nhin nhat.
  useEffect(() => {
    if (slots.length === 0) return;
    const viewTop = scrollTop;
    const viewBottom = scrollTop + Math.max(viewportHeight, 1);
    let best = 0;
    let bestOverlap = -1;
    for (const slot of slots) {
      const overlap =
        Math.min(viewBottom, slot.top + slot.height) - Math.max(viewTop, slot.top);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        best = slot.index;
      }
    }
    onCurrentPageChange(best);
  }, [slots, scrollTop, viewportHeight, onCurrentPageChange]);

  // Yeu cau nhay den trang tu thanh ben / thanh cong cu.
  useEffect(() => {
    if (!scrollRequest) return;
    const el = scrollerRef.current;
    const slot = slots[scrollRequest.pageIndex];
    if (!el || !slot) return;
    el.scrollTo({ top: Math.max(0, slot.top - PAGE_GAP), behavior: 'smooth' });
  }, [scrollRequest, slots]);

  /**
   * Chu thich theo chi so trang cua TAI LIEU GOC.
   *
   * Mot trang bi nhan doi thi chu thich cua no hien tren CA HAI o - dung theo
   * quy tac cua placeAnnotations(), tuc la dung nhu tep xuat ra se co.
   */
  const annotationsByPage = useMemo(() => {
    const map = new Map<number, Annotation[]>();
    for (const a of annotations) {
      const list = map.get(a.pageIndex);
      if (list) list.push(a);
      else map.set(a.pageIndex, [a]);
    }
    return map;
  }, [annotations]);

  const emptyAnnotations = useMemo<Annotation[]>(() => [], []);

  return (
    <div className="docScroller" ref={scrollerRef} onScroll={handleScroll} tabIndex={0}>
      {/* Khoi giu cho: chieu cao dung ngay tu dau nen thanh cuon on dinh. */}
      <div className="docCanvas" style={{ height: `${totalHeight}px` }}>
        {slots.map((slot) => {
          const view = slot.slot;
          const visible = slot.index >= range.start && slot.index <= range.end;
          if (!visible) {
            // Trang ngoai vung: giu cho bang mot the rong. Khong canvas,
            // khong node van ban, khong bo nho anh.
            return (
              <div
                key={slot.index}
                className="pageSpacer"
                style={{
                  top: `${slot.top}px`,
                  width: `${slot.width}px`,
                  height: `${slot.height}px`,
                }}
                aria-hidden="true"
              />
            );
          }
          return (
            <div
              key={slot.index}
              className="pageAnchor"
              style={{ top: `${slot.top}px`, width: `${slot.width}px` }}
            >
              <PageView
                core={core}
                doc={documents.get(view.docId)?.doc ?? null}
                slotIndex={view.slotIndex}
                sourcePageIndex={view.sourcePageIndex}
                originalPageIndex={view.originalPageIndex}
                geometry={slot.geometry}
                active
                draft={fastScrolling}
                annotations={
                  view.originalPageIndex === null
                    ? emptyAnnotations
                    : (annotationsByPage.get(view.originalPageIndex) ?? emptyAnnotations)
                }
                selectedBlockId={selectedBlockId}
                selectedScope={selectedScope}
                onSelectBlock={onSelectBlock}
                onBlocksLoaded={onBlocksLoaded}
                hasTextLayer={view.hasTextLayer}
                tool={tool}
                editSession={editorSlotIndex === view.slotIndex ? editSession : null}
                edited={edited}
                editHints={editHints}
                onRequestEdit={onRequestEdit}
                editRevision={editRevision}
                onInkStroke={onInkStroke}
                onNotePoint={onNotePoint}
                onSelectAnnotation={onSelectAnnotation}
                selectedAnnotationId={selectedAnnotationId}
                onDeselectAnnotation={onDeselectAnnotation}
                onChangeAnnotationRect={onChangeAnnotationRect}
                onDeleteAnnotation={onDeleteAnnotation}
                onShape={onShape}
                onImageRect={onImageRect}
                imageUrl={imageUrl}
                noteDraft={noteSlotIndex === view.slotIndex ? noteDraft : null}
                {...(noteStyle ? { noteStyle } : {})}
                onNoteSave={onNoteSave}
                {...(onNoteAlignChange ? { onNoteAlignChange } : {})}
                onNoteCancel={onNoteCancel}
                embeddedImages={embeddedImages}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function nearestIndex(slots: readonly LaidOutSlot[], top: number): number {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const slot of slots) {
    const distance = Math.abs(slot.top - top);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = slot.index;
    }
  }
  return best;
}
